import { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Layers, Pencil, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { DocFormDialog } from "@/components/admin/DocFormDialog";
import { BulkUploadDialog } from "@/components/admin/BulkUploadDialog";
import { asArray, downloadUrl, errMsg, formatDate, http } from "@/lib/api";

export function DocsManager() {
  const [brands, setBrands] = useState([]);
  const [docs, setDocs] = useState([]);
  const [brandId, setBrandId] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);

  const load = useCallback(() => {
    const q = brandId === "all" ? "" : `?brand_id=${brandId}`;
    http.get(`/admin/documents${q}`).then(({ data }) => setDocs(asArray(data))).catch((e) => toast.error(errMsg(e)));
  }, [brandId]);
  useEffect(() => { http.get("/brands?limit=500").then(({ data }) => setBrands(asArray(data))).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (d) => {
    try { await http.delete(`/admin/documents/${d.id}`); toast.success("Price list deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (d, status) => {
    try {
      await http.post(`/admin/documents/${d.id}/status`, { status });
      toast.success(status === "published" ? "Published" : "Moved back to draft", { description: d.title });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const checkFiles = async () => {
    try {
      const { data } = await http.post("/admin/documents/check-files");
      const bad = asArray(data?.problems);
      if (bad.length) toast.error(`${bad.length} broken ${bad.length === 1 ? "file" : "files"}`, { description: bad.map((p) => `${p.brand_name} — ${p.title} (${p.format})`).join("; "), duration: 12000 });
      else toast.success(`All ${data?.checked ?? 0} files open correctly`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-docs">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Select value={brandId} onValueChange={setBrandId}>
          <SelectTrigger className="h-11 w-full rounded-full bg-ph-surface sm:w-64" data-testid="admin-docs-brand-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brands</SelectItem>
            {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex flex-col gap-2 sm:flex-row">
        <button type="button" onClick={checkFiles} data-testid="admin-check-files-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-ph-line px-5 py-2.5 text-sm font-semibold text-ph-ink hover:bg-ph-raised">
          <ShieldCheck className="h-4 w-4" /> Check files
        </button>
        <button type="button" onClick={() => setBulk(true)} data-testid="admin-bulk-upload-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-ph-ink px-5 py-2.5 text-sm font-semibold text-ph-ink hover:bg-ph-lime hover:text-ph-navy">
          <Layers className="h-4 w-4" /> Bulk upload
        </button>
        <button type="button" onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-upload-doc-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-ph-lime px-5 py-2.5 text-sm font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)]">
          <Upload className="h-4 w-4" /> Upload price list
        </button>
        </div>
      </div>
      <div className="mt-5 overflow-hidden rounded-3xl border border-ph-line bg-ph-surface">
        {docs.length === 0 && <p className="py-14 text-center text-sm text-ph-muted" data-testid="admin-docs-empty">No price lists yet. Upload the first PDF or Excel file.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex flex-col gap-3 border-b border-ph-line px-5 py-4 last:border-b-0 md:flex-row md:items-center" data-testid={`admin-doc-row-${d.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ph-ink">
                <span className={`mr-2 rounded-full px-2 py-0.5 font-mono-ph text-[10px] font-bold uppercase tracking-wider ${d.status === "draft" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"}`} data-testid={`admin-doc-status-${d.id}`}>{d.status === "draft" ? "Draft" : "Published"}</span>
                {d.title} {d.version && <span className="ml-1 font-mono-ph text-xs text-ph-muted">Rev {d.version}</span>}
              </p>
              <p className="text-xs text-ph-muted">{d.brand_name} · {d.category} · Effective {formatDate(d.updated_date)} · Uploaded {formatDate(d.created_at)} · {d.download_count} downloads{d.history?.length ? ` · ${d.history.length} archived versions` : ""}</p>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <button type="button" onClick={() => setStatus(d, d.status === "draft" ? "published" : "draft")} data-testid={`admin-doc-toggle-status-${d.id}`}
                className={`mr-1 rounded-full px-3 py-1.5 text-xs font-semibold ${d.status === "draft" ? "bg-ph-lime text-ph-navy" : "border border-ph-line text-ph-muted hover:text-ph-ink"}`}>
                {d.status === "draft" ? "Publish" : "Unpublish"}
              </button>
              {d.pdf && <a href={downloadUrl(d.id, "pdf")} className="inline-flex items-center gap-1 rounded-full bg-ph-raised px-3 py-1 text-xs font-medium text-ph-ink/85" data-testid={`admin-doc-pdf-${d.id}`}><FileText className="h-3.5 w-3.5" /> PDF</a>}
              {d.excel && <a href={downloadUrl(d.id, "excel")} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800" data-testid={`admin-doc-excel-${d.id}`}><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</a>}
              <button type="button" onClick={() => { setEditing(d); setOpen(true); }} data-testid={`admin-edit-doc-${d.id}`} aria-label="Edit or replace" className="ml-2 rounded-full p-2 text-ph-muted hover:bg-ph-raised hover:text-ph-ink"><Pencil className="h-4 w-4" /></button>
              <ConfirmDelete testId={`admin-delete-doc-${d.id}`} title="Delete this price list?" description={`${d.brand_name} — ${d.title} will no longer be available to download.`} onConfirm={() => remove(d)} />
            </div>
          </div>
        ))}
      </div>
      <DocFormDialog open={open} onOpenChange={setOpen} doc={editing} brands={brands} defaultBrandId={brandId === "all" ? "" : brandId} onSaved={load} />
      <BulkUploadDialog open={bulk} onOpenChange={setBulk} brands={brands} defaultBrandId={brandId === "all" ? "" : brandId} onSaved={load} />
    </div>
  );
}
